import React, { useEffect, useState } from "react";
import { Navigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid,
  LineChart, Line, PieChart, Pie, Cell, Legend,
} from "recharts";
import { Shield, Users, DollarSign, TrendingUp, Activity, ArrowLeft, RefreshCw } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";

const PALETTE = ["#5C7A5A", "#C97B4C", "#B8A78A", "#8B9E88", "#D4B678", "#7A6B58"];

function Stat({ icon: Icon, label, value, sub }) {
  return (
    <div className="p-5 rounded-2xl border border-border bg-card" data-testid={`admin-stat-${label.toLowerCase().replace(/\s+/g,'-')}`}>
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-lg bg-secondary text-primary flex items-center justify-center">
          <Icon className="w-5 h-5" strokeWidth={1.5} />
        </div>
        <div>
          <div className="text-xs uppercase tracking-widest text-muted-foreground">{label}</div>
          <div className="font-heading text-2xl font-medium">{value}</div>
          {sub && <div className="text-xs text-muted-foreground mt-0.5">{sub}</div>}
        </div>
      </div>
    </div>
  );
}

function ChartCard({ title, subtitle, children, testId }) {
  return (
    <div className="p-6 rounded-2xl border border-border bg-card" data-testid={testId}>
      <div className="mb-4">
        <div className="font-heading text-lg font-medium">{title}</div>
        {subtitle && <div className="text-xs text-muted-foreground mt-0.5">{subtitle}</div>}
      </div>
      {children}
    </div>
  );
}

export default function Admin() {
  const { user, loading: authLoading } = useAuth();
  const [data, setData] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const [ov, plan, streak, pw, pwTs, revTs, users] = await Promise.all([
        api.get("/admin/overview"),
        api.get("/admin/plan-cohorts"),
        api.get("/admin/streak-cohorts"),
        api.get("/admin/paywall-summary"),
        api.get("/admin/paywall-timeseries?days=14"),
        api.get("/admin/revenue-timeseries?days=30"),
        api.get("/admin/recent-users?limit=15"),
      ]);
      setData({
        overview: ov.data,
        plan: plan.data,
        streak: streak.data,
        paywall: pw.data,
        paywallTs: pwTs.data,
        revenueTs: revTs.data,
        users: users.data,
      });
    } catch (e) {
      setError(e?.response?.data?.detail || e.message || "Error loading admin data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { if (user?.is_admin) load(); }, [user?.is_admin]);

  if (authLoading) return <div className="min-h-screen flex items-center justify-center text-muted-foreground">Loading…</div>;
  if (!user) return <Navigate to="/login" replace />;
  if (!user.is_admin) {
    return (
      <div className="min-h-screen flex items-center justify-center px-6">
        <div className="max-w-md text-center space-y-4" data-testid="admin-forbidden">
          <Shield className="w-12 h-12 mx-auto text-muted-foreground" strokeWidth={1.2} />
          <h1 className="font-heading text-2xl">Admin only</h1>
          <p className="text-sm text-muted-foreground">You don't have permission to view this page.</p>
          <Link to="/app"><Button variant="outline" data-testid="admin-back-btn">Back to app</Button></Link>
        </div>
      </div>
    );
  }

  const ov = data.overview || {};
  const planData = (data.plan || []).map((p, i) => ({ name: p.plan, value: p.count, fill: PALETTE[i % PALETTE.length] }));
  const streakData = data.streak || [];
  const paywallRows = data.paywall?.by_limit_page || [];

  return (
    <div className="min-h-screen bg-background">
      <header className="sticky top-0 z-30 border-b border-border bg-card/70 backdrop-blur">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-xl bg-accent text-accent-foreground flex items-center justify-center">
              <Shield className="w-5 h-5" strokeWidth={1.5} />
            </div>
            <div>
              <div className="font-heading text-lg font-semibold">Admin Dashboard</div>
              <div className="text-xs text-muted-foreground">NVC Practice · internal</div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="ghost" size="sm" onClick={load} disabled={loading} data-testid="admin-refresh-btn">
              <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
            </Button>
            <Link to="/app"><Button variant="outline" size="sm" className="gap-2" data-testid="admin-app-btn"><ArrowLeft className="w-4 h-4" />App</Button></Link>
          </div>
        </div>
      </header>

      <motion.main
        initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.35 }}
        className="max-w-6xl mx-auto px-6 py-8 space-y-8"
      >
        {error && (
          <div className="p-4 rounded-lg border border-destructive/40 bg-destructive/10 text-sm text-destructive" data-testid="admin-error">
            {error}
          </div>
        )}

        {/* Top KPIs */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-testid="admin-kpis">
          <Stat icon={Users} label="Total users" value={ov.total_users ?? "—"} sub={`+${ov.new_users_7d ?? 0} last 7d`} />
          <Stat icon={TrendingUp} label="Paying users" value={ov.paying_users ?? "—"} sub={`${ov.conversion_rate ?? 0}% conv.`} />
          <Stat icon={DollarSign} label="MRR" value={`$${ov.mrr_usd ?? 0}`} sub="active subscriptions" />
          <Stat icon={Activity} label="Paywall events" value={data.paywall?.total_events ?? 0} sub="all-time" />
        </div>

        {/* Charts row 1 */}
        <div className="grid md:grid-cols-2 gap-4">
          <ChartCard title="Plan cohorts" subtitle="Users by active plan" testId="admin-chart-plans">
            <div className="h-64">
              <ResponsiveContainer minWidth={200} minHeight={200}>
                <PieChart>
                  <Pie data={planData} dataKey="value" nameKey="name" innerRadius={45} outerRadius={85} paddingAngle={3}>
                    {planData.map((entry) => <Cell key={entry.name} fill={entry.fill} />)}
                  </Pie>
                  <Legend />
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </ChartCard>

          <ChartCard title="Streak cohorts" subtitle="Users by current streak length" testId="admin-chart-streaks">
            <div className="h-64">
              <ResponsiveContainer minWidth={200} minHeight={200}>
                <BarChart data={streakData}>
                  <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
                  <XAxis dataKey="bucket" fontSize={11} />
                  <YAxis fontSize={11} allowDecimals={false} />
                  <Tooltip />
                  <Bar dataKey="count" fill={PALETTE[0]} radius={[6, 6, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </ChartCard>
        </div>

        {/* Charts row 2 */}
        <div className="grid md:grid-cols-2 gap-4">
          <ChartCard title="Paywall impressions" subtitle="Last 14 days" testId="admin-chart-paywall-ts">
            <div className="h-64">
              <ResponsiveContainer minWidth={200} minHeight={200}>
                <LineChart data={data.paywallTs || []}>
                  <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
                  <XAxis dataKey="date" fontSize={10} />
                  <YAxis fontSize={11} allowDecimals={false} />
                  <Tooltip />
                  <Line type="monotone" dataKey="count" stroke={PALETTE[1]} strokeWidth={2} dot={{ r: 3 }} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </ChartCard>

          <ChartCard title="Daily revenue" subtitle="Last 30 days · paid transactions" testId="admin-chart-revenue">
            <div className="h-64">
              <ResponsiveContainer minWidth={200} minHeight={200}>
                <BarChart data={data.revenueTs || []}>
                  <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
                  <XAxis dataKey="date" fontSize={10} />
                  <YAxis fontSize={11} />
                  <Tooltip formatter={(v) => `$${v}`} />
                  <Bar dataKey="revenue" fill={PALETTE[2]} radius={[6, 6, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </ChartCard>
        </div>

        {/* Paywall table */}
        <ChartCard title="Paywall events breakdown" subtitle="Which limits trigger the upgrade dialog most" testId="admin-paywall-table">
          {paywallRows.length === 0 ? (
            <div className="text-sm text-muted-foreground py-8 text-center">No paywall events logged yet.</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="text-xs uppercase tracking-wider text-muted-foreground">
                  <tr>
                    <th className="text-left py-2">Limit</th>
                    <th className="text-left py-2">Page</th>
                    <th className="text-right py-2">Events</th>
                  </tr>
                </thead>
                <tbody>
                  {paywallRows.map((r, i) => (
                    <tr key={`${r.limit_key}-${r.page}`} className="border-t border-border" data-testid={`admin-paywall-row-${i}`}>
                      <td className="py-2 font-medium">{r.limit_key}</td>
                      <td className="py-2 text-muted-foreground">{r.page}</td>
                      <td className="py-2 text-right font-mono">{r.count}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </ChartCard>

        {/* Recent users */}
        <ChartCard title="Recent signups" subtitle="Last 15 users" testId="admin-recent-users">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-xs uppercase tracking-wider text-muted-foreground">
                <tr>
                  <th className="text-left py-2">Email</th>
                  <th className="text-left py-2">Name</th>
                  <th className="text-left py-2">Lang</th>
                  <th className="text-left py-2">Plan</th>
                  <th className="text-right py-2">Points</th>
                  <th className="text-right py-2">Joined</th>
                </tr>
              </thead>
              <tbody>
                {(data.users || []).map((u, i) => (
                  <tr key={u.id || i} className="border-t border-border" data-testid={`admin-user-row-${i}`}>
                    <td className="py-2 truncate max-w-[220px]">{u.email}</td>
                    <td className="py-2 text-muted-foreground">{u.name}</td>
                    <td className="py-2 text-muted-foreground uppercase text-xs">{u.language}</td>
                    <td className="py-2">
                      <span className={`text-xs px-2 py-0.5 rounded-full ${u.effective_plan === 'free' ? 'bg-secondary text-muted-foreground' : 'bg-accent/20 text-accent'}`}>
                        {u.effective_plan}
                      </span>
                    </td>
                    <td className="py-2 text-right font-mono">{u.points ?? 0}</td>
                    <td className="py-2 text-right text-xs text-muted-foreground">{(u.created_at || "").slice(0, 10)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </ChartCard>

        <div className="text-center text-xs text-muted-foreground py-4">
          Refreshed at {new Date().toLocaleTimeString()}
        </div>
      </motion.main>
    </div>
  );
}
